import React from 'react';
import { MapPinIcon, XIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface LocationPermissionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAllow: () => void;
}
export function LocationPermissionModal({
  isOpen,
  onClose,
  onAllow
}: LocationPermissionModalProps) {
  return <AnimatePresence data-id="element-4152" data-mp="3abw8rqbq7sw6">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 z-[250] backdrop-blur-sm" onClick={onClose} data-id="element-4153" data-mp="au512d7deecy" />

          {/* Bottom Sheet */}
          <motion.div initial={{
        y: '100%'
      }} animate={{
        y: 0
      }} exit={{
        y: '100%'
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 200
      }} className="fixed bottom-0 left-0 right-0 z-[260] bg-white rounded-t-3xl shadow-2xl overflow-hidden max-w-lg mx-auto md:mb-6 md:rounded-3xl" onClick={e => e.stopPropagation()} data-id="element-4154" data-mp="uj72buvchlxr">
            {/* Handle for dragging feel */}
            <div className="w-12 h-1.5 bg-gray-200 rounded-full mx-auto mt-3 mb-1 md:hidden" data-id="element-4155" data-mp="3poc1u4tp0ou7" />

            <div className="p-6 pt-4 md:pt-6 relative" data-id="element-4156" data-mp="3bt9x3l0l15iz">
              <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors" data-id="element-4157" data-mp="3gqtcio90noci">
                <XIcon className="w-5 h-5 text-gray-500" data-id="element-4158" data-mp="36r2g2h0waz9s" />
              </button>

              <div className="flex justify-center mb-6" data-id="element-4159" data-mp="10z5gqw5qo1va">
                <div className="w-20 h-20 bg-gradient-to-br from-[#1E2B55] to-[#162041] rounded-full flex items-center justify-center shadow-xl ring-4 ring-blue-50" data-id="element-4160" data-mp="10znkseyn7kdr">
                  <MapPinIcon className="w-10 h-10 text-white" data-id="element-4161" data-mp="2dyh32z19kvg5" />
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 text-center mb-3" data-id="element-4162" data-mp="28x9sytbnfk6b">
                Enable Location Services
              </h2>
              <p className="text-gray-600 text-center mb-8 px-2" data-id="element-4163" data-mp="24yu2ugk9tvoo">
                We need your location to show you nearby pharmacies, accurate
                delivery times, and available products in your area.
              </p>

              <div className="bg-gray-50 rounded-2xl p-5 mb-8 space-y-4" data-id="element-4164" data-mp="2slse8ztjadfq">
                <div className="flex items-center gap-4" data-id="element-4165" data-mp="2sooewvwrng7v">
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-4166" data-mp="2nualgz1nzj5u">
                    <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-4167" data-mp="2iw8vrv9qglle">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" data-id="element-4168" data-mp="3iwayp6rgl8yh" />
                    </svg>
                  </div>
                  <p className="text-base font-medium text-gray-700" data-id="element-4169" data-mp="3swlcasheoie6">
                    Find nearest pharmacy branches
                  </p>
                </div>
                <div className="flex items-center gap-4" data-id="element-4170" data-mp="5org198cgg13">
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-4171" data-mp="aovuktpoz0oo">
                    <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-4172" data-mp="ff2jkuo48n7g">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" data-id="element-4173" data-mp="ohljb5wlujph" />
                    </svg>
                  </div>
                  <p className="text-base font-medium text-gray-700" data-id="element-4174" data-mp="brky6rhevwjb">
                    Get accurate delivery estimates
                  </p>
                </div>
                <div className="flex items-center gap-4" data-id="element-4175" data-mp="pkakyy4rl5hh">
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-4176" data-mp="kko6ymh43l7m">
                    <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-4177" data-mp="3szgcewufga6y">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" data-id="element-4178" data-mp="6plv7ipl31mw" />
                    </svg>
                  </div>
                  <p className="text-base font-medium text-gray-700" data-id="element-4179" data-mp="1d7shf6yz4rqs">
                    View products available near you
                  </p>
                </div>
              </div>

              <div className="space-y-3 mb-6" data-id="element-4180" data-mp="306bgbutetbxp">
                <button onClick={onAllow} className="w-full bg-[#1E2B55] text-white font-bold py-4 rounded-2xl hover:bg-[#162041] transition-all shadow-lg shadow-blue-900/20 active:scale-[0.98]" data-id="element-4181" data-mp="2m58qd6zanhhi">
                  Allow Location Access
                </button>
                <button onClick={onClose} className="w-full bg-gray-100 text-gray-900 font-bold py-4 rounded-2xl hover:bg-gray-200 transition-all active:scale-[0.98]" data-id="element-4182" data-mp="3ivrggh16n2dl">
                  Not Now
                </button>
              </div>

              <p className="text-xs text-gray-400 text-center pb-2" data-id="element-4183" data-mp="1ne6dg5f2cik">
                Your location data is secure and only used to improve your
                shopping experience
              </p>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}